import React, { useState, useEffect } from "react";
import { ProjectService } from "../services/projectService";
import { formatDate } from "../utils/dateFormatting";
import { formatJobRevNumber } from '../utils/projectFormatting';
import { Modal, ModalBody, ModalFooter, Input, Dropdown, Button } from "./ui";

const AgreementReadyModal = ({
  project,
  clients,
  contacts,
  employees,
  isOpen,
  onClose,
  onSave,
  theme = 'dark',
}) => {
  const [formData, setFormData] = useState({
    modelingStatus: "",
    billingStatus: "",
    agreementStatus: "",
    initialAcceptance: "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Reference data (read-only)
  const [clientInfo, setClientInfo] = useState({
    clientName: "",
    clientOf: "",
    contactName: "",
    contactEmail: "",
    clientAddress: "",
    modelPrice: "",
  });

  useEffect(() => {
    if (project) {
      // Get client information if available
      const client = clients?.find(c => c.id === project.clientId || c.name === project.clientName);

      setFormData({
        modelingStatus: project.modelingStatus || "",
        billingStatus: project.billingStatus || "",
        agreementStatus: project.agreementStatus || "",
        initialAcceptance: project.initialAcceptance
          ? formatDate(project.initialAcceptance, "input")
          : "",
      });

      setClientInfo({
        clientName: project.clientName || "",
        clientOf: project.clientOf || client?.client_of || "",
        contactName: project.contactName || "",
        contactEmail: project.contact?.email || "",
        clientAddress: client?.address || "",
        modelPrice: project.modelPrice || project.price || "",
      });
    }
  }, [project, clients]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = async () => {
    const fieldsToUpdate = {
      initial_acceptance: formData.initialAcceptance || null,
      modeling_status: formData.modelingStatus || null,
      billing_status: formData.billingStatus || null,
      agreement_status: formData.agreementStatus || null,
    };

    try {
      setSaving(true);
      setError(null);

      for (const [field, value] of Object.entries(fieldsToUpdate)) {
        let currentFieldName;
        if (field === 'initial_acceptance') {
          currentFieldName = 'initialAcceptance';
        } else if (field === 'modeling_status') {
          currentFieldName = 'modelingStatus';
        } else if (field === 'billing_status') {
          currentFieldName = 'billingStatus';
        } else if (field === 'agreement_status') {
          currentFieldName = 'agreementStatus';
        } else {
          currentFieldName = field.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
        }

        if (value !== project[currentFieldName]) {
          console.log(`Updating field ${field} from "${project[currentFieldName]}" to "${value}"`);
          try {
            await ProjectService.updateProject(project.id, field, value);
          } catch (fieldError) {
            console.error(`Error updating field ${field}:`, fieldError);
            throw new Error(`Failed to update ${field}: ${fieldError.message}`);
          }
        }
      }

      if (onSave) {
        await onSave();
      }
      onClose();
    } catch (err) {
      console.error("Error saving project:", err);
      setError(`Failed to save changes: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const formatPrice = (price) => {
    if (!price && price !== 0) return "TBD";
    return `$${parseFloat(price).toLocaleString()}`;
  };

  if (!project) return null;

  return (
    <Modal trackAs="AgreementReadyModal" isOpen={isOpen} onClose={onClose} size="lg" theme={theme}>
      {/* Standard Project Modal Header */}
      <div
        style={{
          padding: 'var(--space-6)',
          borderBottom: '1px solid var(--gray-700)',
          background: 'linear-gradient(to bottom, var(--dark-bg), var(--dark-surface))',
          borderTopLeftRadius: 'var(--radius-xl)',
          borderTopRightRadius: 'var(--radius-xl)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
              <span
                style={{
                  fontSize: 'var(--text-2xl)',
                  fontWeight: 'var(--font-semibold)',
                  color: 'var(--gray-400)',
                  flexShrink: 0,
                }}
              >
                {formatJobRevNumber(project.jobNumber, project.rev)}
              </span>
              <h1
                style={{
                  margin: 0,
                  fontSize: 'var(--text-2xl)',
                  fontWeight: 'var(--font-semibold)',
                  color: 'var(--gray-50)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  flex: 1,
                  minWidth: 0,
                }}
              >
                {project.projectName || project.name}
              </h1>
            </div>
            <p
              style={{
                margin: 0,
                marginTop: 'var(--space-2)',
                fontSize: 'var(--text-sm)',
                color: 'var(--gray-400)',
              }}
            >
              Agreement Ready
            </p>
          </div>
        </div>
      </div>

      <ModalBody>
        {error && (
          <div style={{
            padding: 'var(--space-4)',
            marginBottom: 'var(--space-4)',
            background: 'var(--danger-bg, #dc26261a)',
            border: '1px solid var(--danger, #dc2626)',
            borderRadius: 'var(--radius-lg)',
            color: 'var(--danger, #dc2626)',
            fontSize: 'var(--text-sm)',
          }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}>
          {/* Project Information Section */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
            <h2 style={{
              margin: 0,
              fontSize: 'var(--text-lg)',
              fontWeight: 'var(--font-semibold)',
              color: 'var(--gray-200)',
              paddingBottom: 'var(--space-2)',
              borderBottom: '1px solid var(--gray-700)',
            }}>
              Project Information
            </h2>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-4)' }}>
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: 'var(--space-2)',
                  fontSize: 'var(--text-sm)',
                  fontWeight: 'var(--font-medium)',
                  color: 'var(--gray-300)'
                }}>
                  Model Price
                </label>
                <div style={{
                  padding: 'var(--space-3)',
                  background: 'var(--dark-elevated)',
                  border: '1px solid var(--gray-600)',
                  borderRadius: 'var(--radius-md)',
                  fontSize: 'var(--text-sm)',
                  color: 'var(--gray-300)',
                }}>
                  {formatPrice(clientInfo.modelPrice)}
                </div>
              </div>
            </div>
          </div>

          {/* Client Information Section */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
            <h2 style={{
              margin: 0,
              fontSize: 'var(--text-lg)',
              fontWeight: 'var(--font-semibold)',
              color: 'var(--gray-200)',
              paddingBottom: 'var(--space-2)',
              borderBottom: '1px solid var(--gray-700)',
            }}>
              Client Information
            </h2>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-4)' }}>
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: 'var(--space-2)',
                  fontSize: 'var(--text-sm)',
                  fontWeight: 'var(--font-medium)',
                  color: 'var(--gray-300)'
                }}>
                  Client
                </label>
                <div style={{
                  padding: 'var(--space-3)',
                  background: 'var(--dark-elevated)',
                  border: '1px solid var(--gray-600)',
                  borderRadius: 'var(--radius-md)',
                  fontSize: 'var(--text-sm)',
                  color: 'var(--gray-300)',
                }}>
                  {clientInfo.clientName || 'Not Set'}
                </div>
              </div>

              <div>
                <label style={{
                  display: 'block',
                  marginBottom: 'var(--space-2)',
                  fontSize: 'var(--text-sm)',
                  fontWeight: 'var(--font-medium)',
                  color: 'var(--gray-300)'
                }}>
                  Client Of
                </label>
                <div style={{
                  padding: 'var(--space-3)',
                  background: 'var(--dark-elevated)',
                  border: '1px solid var(--gray-600)',
                  borderRadius: 'var(--radius-md)',
                  fontSize: 'var(--text-sm)',
                  color: 'var(--gray-300)',
                }}>
                  {clientInfo.clientOf || 'Not Set'}
                </div>
              </div>

              <div>
                <label style={{
                  display: 'block',
                  marginBottom: 'var(--space-2)',
                  fontSize: 'var(--text-sm)',
                  fontWeight: 'var(--font-medium)',
                  color: 'var(--gray-300)'
                }}>
                  Project Contact
                </label>
                <div style={{
                  padding: 'var(--space-3)',
                  background: 'var(--dark-elevated)',
                  border: '1px solid var(--gray-600)',
                  borderRadius: 'var(--radius-md)',
                  fontSize: 'var(--text-sm)',
                  color: 'var(--gray-300)',
                }}>
                  <div>{clientInfo.contactName || 'No contact assigned'}</div>
                  {clientInfo.contactEmail && (
                    <div style={{ fontSize: 'var(--text-xs)', color: 'var(--gray-400)', marginTop: 'var(--space-1)' }}>
                      {clientInfo.contactEmail}
                    </div>
                  )}
                </div>
              </div>

              <div style={{ gridColumn: '1 / -1' }}>
                <label style={{
                  display: 'block',
                  marginBottom: 'var(--space-2)',
                  fontSize: 'var(--text-sm)',
                  fontWeight: 'var(--font-medium)',
                  color: 'var(--gray-300)'
                }}>
                  Client Address
                </label>
                <div style={{
                  padding: 'var(--space-3)',
                  background: 'var(--dark-elevated)',
                  border: '1px solid var(--gray-600)',
                  borderRadius: 'var(--radius-md)',
                  fontSize: 'var(--text-sm)',
                  color: 'var(--gray-300)',
                  minHeight: '50px',
                  whiteSpace: 'pre-wrap',
                }}>
                  {clientInfo.clientAddress || 'No address on file'}
                </div>
              </div>
            </div>
          </div>

          {/* Status Information Section */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
            <h2 style={{
              margin: 0,
              fontSize: 'var(--text-lg)',
              fontWeight: 'var(--font-semibold)',
              color: 'var(--gray-200)',
              paddingBottom: 'var(--space-2)',
              borderBottom: '1px solid var(--gray-700)',
            }}>
              Status Information
            </h2>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-4)' }}>
              <Dropdown
                label="Modeling Status"
                value={formData.modelingStatus}
                onChange={(value) => handleInputChange("modelingStatus", value)}
                options={[
                  { value: "", label: "Not Set" },
                  { value: "Startup", label: "Startup" },
                  { value: "Data Prep", label: "Data Prep" },
                  { value: "Ready to Model", label: "Ready to Model" },
                  { value: "Modeling", label: "Modeling" },
                  { value: "Double Check", label: "Double Check" },
                  { value: "Corrections", label: "Corrections" },
                  { value: "Review", label: "Review" },
                  { value: "Ready to Export", label: "Ready to Export" },
                  { value: "Utilities", label: "Utilities" },
                  { value: "Completed", label: "Completed" },
                ]}
              />

              <Dropdown
                label="Billing Status"
                value={formData.billingStatus}
                onChange={(value) => handleInputChange("billingStatus", value)}
                options={[
                  { value: "", label: "Not Set" },
                  { value: "Quoted", label: "Quoted" },
                  { value: "Invoiced", label: "Invoiced" },
                  { value: "Paid", label: "Paid" },
                  { value: "Voided", label: "Voided" },
                ]}
              />

              <Dropdown
                label="Agreement Status"
                value={formData.agreementStatus}
                onChange={(value) => handleInputChange("agreementStatus", value)}
                options={[
                  { value: "", label: "Not Set" },
                  { value: "Ready", label: "Ready" },
                  { value: "Utility Ready", label: "Utility Ready" },
                  { value: "Sent", label: "Sent" },
                  { value: "Completed", label: "Completed" },
                  { value: "None needed", label: "None needed" },
                  { value: "Revision-none", label: "Revision-none" },
                  { value: "Voided", label: "Voided" },
                  { value: "Hold", label: "Hold" },
                  { value: "Not Signed", label: "Not Signed" },
                ]}
              />

              <Input
                label="Initial Acceptance"
                type="date"
                value={formData.initialAcceptance}
                onChange={(e) => handleInputChange("initialAcceptance", e.target.value)}
              />
            </div>
          </div>
        </div>
      </ModalBody>

      <ModalFooter>
        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          width: '100%',
          gap: 'var(--space-3)',
        }}>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSave} loading={saving}>
            Save Changes
          </Button>
        </div>
      </ModalFooter>
    </Modal>
  );
};

export default AgreementReadyModal;
